import moment from "moment";

/** Day widths per view type */
export const DAY_WIDTHS = {
  day: 200,
  week: 90,
  month: 27,
};

/**
 * Returns the default date range for a view type.
 * day   → current month (start to end)
 * week  → current quarter
 * month → full year
 */
export function getDefaultDateRange(viewType) {
  const now = moment();
  switch (viewType) {
    case "day":
      return {
        startDate: now.clone().startOf("month").format("YYYY-MM-DD"),
        endDate: now.clone().endOf("month").format("YYYY-MM-DD"),
      };
    case "week":
      return {
        startDate: now.clone().startOf("quarter").format("YYYY-MM-DD"),
        endDate: now.clone().endOf("quarter").format("YYYY-MM-DD"),
      };
    case "month":
      return {
        startDate: now.clone().startOf("year").format("YYYY-MM-DD"),
        endDate: now.clone().endOf("year").format("YYYY-MM-DD"),
      };
    default:
      return {
        startDate: now.clone().startOf("quarter").format("YYYY-MM-DD"),
        endDate: now.clone().endOf("quarter").format("YYYY-MM-DD"),
      };
  }
}

/**
 * Get a buffered date range (enclosing quarter(s)) for a custom date range.
 */
export function getBufferedDateRange(startDate, endDate) {
  const start = moment(startDate);
  const end = moment(endDate);
  return {
    startDate: start.clone().startOf("quarter").format("YYYY-MM-DD"),
    endDate: end.clone().endOf("quarter").format("YYYY-MM-DD"),
  };
}

/**
 * Navigate date range by direction (-1 = prev, +1 = next).
 * Shifts by the same unit as the view type default span.
 */
export function navigateDateRange(viewType, startDate, endDate, direction) {
  const start = moment(startDate);
  const end = moment(endDate);
  const diff = end.diff(start, "days") + 1;

  return {
    startDate: start.clone().add(direction * diff, "days").format("YYYY-MM-DD"),
    endDate: end.clone().add(direction * diff, "days").format("YYYY-MM-DD"),
  };
}

/**
 * Generate all days between startDate and endDate inclusive.
 * Returns array of moment objects.
 */
export function generateDays(startDate, endDate) {
  const days = [];
  const current = moment(startDate);
  const last = moment(endDate);
  while (current.isSameOrBefore(last, "day")) {
    days.push(current.clone());
    current.add(1, "day");
  }
  return days;
}

/**
 * Group days by month. Returns [{ month, year, label, days }].
 */
export function groupDaysByMonth(days) {
  const groups = [];
  let current = null;
  for (const day of days) {
    const key = day.format("YYYY-MM");
    if (!current || current.key !== key) {
      current = {
        key,
        label: day.format("MMM YYYY"),
        month: day.month(),
        year: day.year(),
        days: [],
      };
      groups.push(current);
    }
    current.days.push(day);
  }
  return groups;
}

/**
 * Group days by week. Returns [{ label, days, weekNumber }].
 */
export function groupDaysByWeek(days) {
  const groups = [];
  let current = null;
  for (const day of days) {
    const weekKey = day.clone().startOf("isoWeek").format("YYYY-[W]WW");
    if (!current || current.key !== weekKey) {
      current = {
        key: weekKey,
        weekNumber: day.isoWeek(),
        days: [],
        label: "", // set after
      };
      groups.push(current);
    }
    current.days.push(day);
  }
  // Set labels
  groups.forEach((g) => {
    const first = g.days[0];
    const last = g.days[g.days.length - 1];
    g.label = `${first.format("MMM D")} - ${last.format("MMM D")}`;
  });
  return groups;
}

/**
 * Check if a day is a weekend (Saturday or Sunday).
 */
export function isWeekend(day) {
  const d = day.day();
  return d === 0 || d === 6;
}

/**
 * Check if a day is today.
 */
export function isToday(day) {
  return day.isSame(moment(), "day");
}

/**
 * Get the pixel X position of a date within the timeline.
 * Returns null if the date is outside the range.
 */
export function getDatePosition(date, timelineStartDate, dayWidth) {
  const d = moment(date);
  const start = moment(timelineStartDate);
  if (d.isBefore(start, "day")) return null;
  const dayIndex = d.diff(start, "days");
  return dayIndex * dayWidth;
}

/**
 * Get the pixel width spanning from startDate to endDate.
 */
export function getDateSpanWidth(startDate, endDate, dayWidth) {
  const start = moment(startDate);
  const end = moment(endDate);
  const days = end.diff(start, "days") + 1;
  return days * dayWidth;
}

/**
 * Convert a pixel X position back to a date.
 */
export function positionToDate(x, timelineStartDate, dayWidth) {
  const dayIndex = Math.floor(x / dayWidth);
  return moment(timelineStartDate).add(dayIndex, "days").format("YYYY-MM-DD");
}

/**
 * Get weeks within a date range for capacity calculation.
 * Returns [{ weekStart, weekEnd, key }].
 */
export function getWeeksInRange(startDate, endDate) {
  const weeks = [];
  const current = moment(startDate).clone().startOf("isoWeek");
  const last = moment(endDate);
  while (current.isSameOrBefore(last, "day")) {
    const weekEnd = current.clone().endOf("isoWeek");
    weeks.push({
      key: current.format("YYYY-[W]WW"),
      weekStart: current.format("YYYY-MM-DD"),
      weekEnd: weekEnd.format("YYYY-MM-DD"),
    });
    current.add(1, "week");
  }
  return weeks;
}

/**
 * Sum allocated hours across a flat list of tasks within [rs, re].
 * Shared by all the range-based allocation calculators below.
 * @param {Array} tasks
 * @param {moment.Moment} rs
 * @param {moment.Moment} re
 * @returns {number} total hours (not rounded)
 */
function sumAllocatedHoursInRange(tasks, rs, re) {
  let total = 0;

  for (const task of (tasks || [])) {
    const alloc = task.allocation;
    if (!alloc || !alloc.startDate || !alloc.endDate) continue;

    const allocStart = moment(alloc.startDate);
    const allocEnd = moment(alloc.endDate);

    const overlapStart = moment.max(rs, allocStart);
    const overlapEnd = moment.min(re, allocEnd);

    if (overlapStart.isAfter(overlapEnd, "day")) continue;

    let workingDays = 0;
    const cur = overlapStart.clone();
    while (cur.isSameOrBefore(overlapEnd, "day")) {
      const dow = cur.day();
      if (alloc.isIncludeWeekends || (dow !== 0 && dow !== 6)) {
        workingDays++;
      }
      cur.add(1, "day");
    }

    total += workingDays * (alloc.hoursPerDay || 0);
  }

  return total;
}

/**
 * Calculate allocated hours per week for a resource.
 * @param {Array} projects - resource's projects array from API
 * @param {string} weekStart
 * @param {string} weekEnd
 * @returns {number} total hours in that week
 */
export function calculateWeeklyAllocatedHours(projects, weekStart, weekEnd) {
  const ws = moment(weekStart);
  const we = moment(weekEnd);
  let total = 0;

  for (const project of (projects || [])) {
    for (const scope of (project.scopes || [])) {
      total += sumAllocatedHoursInRange(scope.tasks, ws, we);
      for (const group of (scope.taskGroups || [])) {
        total += sumAllocatedHoursInRange(group.tasks, ws, we);
      }
    }
  }
  return total;
}

/**
 * Get the date range for a summary period key.
 * @param {string} period - one of: last_week, this_week, next_week, last_month, this_month, next_month
 * @returns {{ startDate: string, endDate: string, label: string }}
 */
export function getSummaryPeriodRange(period) {
  const now = moment();
  switch (period) {
    case "last_week":
      return {
        startDate: now.clone().subtract(1, "week").startOf("isoWeek").format("YYYY-MM-DD"),
        endDate: now.clone().subtract(1, "week").endOf("isoWeek").format("YYYY-MM-DD"),
        label: "Last week",
      };
    case "this_week":
      return {
        startDate: now.clone().startOf("isoWeek").format("YYYY-MM-DD"),
        endDate: now.clone().endOf("isoWeek").format("YYYY-MM-DD"),
        label: "This week",
      };
    case "next_week":
      return {
        startDate: now.clone().add(1, "week").startOf("isoWeek").format("YYYY-MM-DD"),
        endDate: now.clone().add(1, "week").endOf("isoWeek").format("YYYY-MM-DD"),
        label: "Next week",
      };
    case "last_month":
      return {
        startDate: now.clone().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
        endDate: now.clone().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
        label: "Last month",
      };
    case "this_month":
      return {
        startDate: now.clone().startOf("month").format("YYYY-MM-DD"),
        endDate: now.clone().endOf("month").format("YYYY-MM-DD"),
        label: "This month",
      };
    case "next_month":
      return {
        startDate: now.clone().add(1, "month").startOf("month").format("YYYY-MM-DD"),
        endDate: now.clone().add(1, "month").endOf("month").format("YYYY-MM-DD"),
        label: "Next month",
      };
    default:
      return {
        startDate: now.clone().startOf("isoWeek").format("YYYY-MM-DD"),
        endDate: now.clone().endOf("isoWeek").format("YYYY-MM-DD"),
        label: "This week",
      };
  }
}

/**
 * Calculate total allocated hours for a resource within an arbitrary date range.
 * Works regardless of whether the range overlaps the fetched timeline range.
 * @param {Array} projects - resource's projects array
 * @param {string} rangeStart - YYYY-MM-DD
 * @param {string} rangeEnd - YYYY-MM-DD
 * @returns {{ allocated: number, capacity: number }}
 */
export function calculateAllocatedHoursInRange(projects, rangeStart, rangeEnd, maxWeeklyHours = 40) {
  let totalAllocated = 0;
  const rs = moment(rangeStart);
  const re = moment(rangeEnd);

  for (const project of (projects || [])) {
    for (const scope of (project.scopes || [])) {
      totalAllocated += sumAllocatedHoursInRange(scope.tasks, rs, re);
      for (const group of (scope.taskGroups || [])) {
        totalAllocated += sumAllocatedHoursInRange(group.tasks, rs, re);
      }
    }
  }

  // Calculate capacity: count working days in range * (maxWeeklyHours / 5)
  const hoursPerDay = maxWeeklyHours / 5;
  let capacityDays = 0;
  const cur = rs.clone();
  while (cur.isSameOrBefore(re, "day")) {
    const dow = cur.day();
    if (dow !== 0 && dow !== 6) capacityDays++;
    cur.add(1, "day");
  }

  return {
    allocated: parseFloat(totalAllocated.toFixed(2)),
    capacity: parseFloat((capacityDays * hoursPerDay).toFixed(2)),
  };
}

/**
 * Calculate total allocated hours for a single project within an arbitrary date range.
 * @param {object} project - a project object with scopes/tasks
 * @param {string} rangeStart - YYYY-MM-DD
 * @param {string} rangeEnd - YYYY-MM-DD
 * @returns {number} allocated hours
 */
export function calculateProjectAllocatedHoursInRange(project, rangeStart, rangeEnd) {
  const rs = moment(rangeStart);
  const re = moment(rangeEnd);
  let totalAllocated = 0;

  for (const scope of (project.scopes || [])) {
    totalAllocated += sumAllocatedHoursInRange(scope.tasks, rs, re);
    for (const group of (scope.taskGroups || [])) {
      totalAllocated += sumAllocatedHoursInRange(group.tasks, rs, re);
    }
  }

  return parseFloat(totalAllocated.toFixed(2));
}

/**
 * Calculate total allocated hours for a single scope within an arbitrary date range.
 * Includes tasks in the scope's task groups.
 */
export function calculateScopeAllocatedHoursInRange(scope, rangeStart, rangeEnd) {
  const rs = moment(rangeStart);
  const re = moment(rangeEnd);
  let totalAllocated = sumAllocatedHoursInRange(scope.tasks, rs, re);

  for (const group of (scope.taskGroups || [])) {
    totalAllocated += sumAllocatedHoursInRange(group.tasks, rs, re);
  }

  return parseFloat(totalAllocated.toFixed(2));
}

/**
 * Calculate total allocated hours for a single task group within an arbitrary date range.
 */
export function calculateTaskGroupAllocatedHoursInRange(group, rangeStart, rangeEnd) {
  const rs = moment(rangeStart);
  const re = moment(rangeEnd);
  const totalAllocated = sumAllocatedHoursInRange(group.tasks, rs, re);

  return parseFloat(totalAllocated.toFixed(2));
}

/**
 * Calculate allocated hours for a single task within an arbitrary date range.
 */
export function calculateTaskAllocatedHoursInRange(task, rangeStart, rangeEnd) {
  const alloc = task.allocation;
  if (!alloc || !alloc.startDate || !alloc.endDate) return 0;

  const rs = moment(rangeStart);
  const re = moment(rangeEnd);
  const allocStart = moment(alloc.startDate);
  const allocEnd = moment(alloc.endDate);
  const overlapStart = moment.max(rs, allocStart);
  const overlapEnd = moment.min(re, allocEnd);
  if (overlapStart.isAfter(overlapEnd, "day")) return 0;

  let workingDays = 0;
  const cur = overlapStart.clone();
  while (cur.isSameOrBefore(overlapEnd, "day")) {
    const dow = cur.day();
    if (alloc.isIncludeWeekends || (dow !== 0 && dow !== 6)) workingDays++;
    cur.add(1, "day");
  }

  return parseFloat((workingDays * (alloc.hoursPerDay || 0)).toFixed(2));
}
